import { useState, useEffect } from "react"
import { useForm, FormProvider, UseFormReturn } from "react-hook-form"
import { useMutation } from "react-query"

// Constants
import { STATUS } from "~constants/status"
import { FORM_STEPS } from "~constants/company-form"

// Components
import Modal from "~app/Modal"
import CompletedIcon from "~images/icons/completed.svg?react"
import CompanyModalStep1 from "~components/companies/CompanyModalStep1"
import CompanyModalStep3 from "~components/companies/CompanyModalStep3"
import CompanyModalStep4 from "~components/companies/CompanyModalStep4"
import { ErrorMessageSmall } from "~styled/errors"
import { ModalSuccessContainer, ModalSuccessTitle } from "~styled/modal"
import { WizardContainer, WizardTitle, WizardForm } from "~styled/wizard"

// Services
import CompanyService from "~services/CompanyService"
import LocationService from "~services/LocationService"

// Utils
import { SelectOption } from "~utils/dropdown"
import { processImage } from "~utils/utils"
import {
  step1DefaultValues,
  step1PrefillValues,
  step3PrefillValues,
  step4PrefillValues,
} from "~components/companies/utils"

// Shapes
import { LocationShape } from "~shapes/location"
import { OnRequestCloseProps } from "~shapes/modal"
import { CompanyShape, PostCompanyShape } from "~shapes/company"
import useCompanyWithLocation from "~hooks/useCompanyWithLocation"
import { toast } from "react-toastify"

type CompanyProps = {
  companyId?: string
  onRequestClose: (props: OnRequestCloseProps) => void
}

export type CompanyData = {
  name: string
  code: string
  description: string
  exchange: SelectOption
  symbol: string
  country: SelectOption
  region: SelectOption
  city: string
  zipCode: string
  logo: string
  cover: string
  contactName: string
  contactEmail: string
  contactPhone: string
  color: string
  about?: string
}

const CompanyModal = ({ companyId, onRequestClose }: CompanyProps) => {
  const [formData, setFormData] = useState<CompanyData | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [companyError, setCompanyError] = useState<string>("")
  const [nextButtonLabel, setNextButtonLabel] = useState<string>("Next")
  const [prevButtonLabel, setPrevButtonLabel] = useState<string>("Cancel")
  const [showCancelButton, setShowCancelButton] = useState<boolean>(true)
  const [wizardStepNumber, setWizardStepNumber] = useState<number>(1)

  const {
    data: company,
    isFetching: isFetchingCompany,
    refetch: fetchCompany,
    isError: isErrorFetchingCompany,
  } = useCompanyWithLocation(companyId)
  const { isLoading: isCreatingCompany, mutateAsync: mutatePostCompany } =
    useMutation("post-company", (payload: PostCompanyShape) =>
      CompanyService.postCompany(payload)
    )
  const { isLoading: isUpdatingCompany, mutateAsync: mutatePatchCompany } =
    useMutation(
      "update-company",
      (payload: Partial<CompanyShape>) => CompanyService.patchCompany(payload),
      {
        onError: () => setCompanyError("Error"),
      }
    )

  useEffect(() => {
    ;(async () => {
      if (companyId) await fetchCompany()
    })()
  }, [])

  // Effect to fill form with an existing Company
  useEffect(() => {
    if (company) {
      step1Methods.reset(step1PrefillValues(company))
      // step2Methods.reset(step2PrefillValues(company))
      step3Methods.reset(step3PrefillValues(company))
      step4Methods.reset(step4PrefillValues(company))
    }
  }, [company])

  useEffect(() => {
    if (isErrorFetchingCompany) {
      toast.error(`Failed to load the company`)
    }
  }, [isErrorFetchingCompany])

  useEffect(() => {
    setIsLoading(isFetchingCompany || isCreatingCompany || isUpdatingCompany)
  }, [isFetchingCompany, isCreatingCompany, isUpdatingCompany])

  // Effect to handle Company creation
  useEffect(() => {
    ;(async () => {
      if (formData) {
        if (wizardStepNumber === FORM_STEPS.THIRD) {
          await handleCompanySave(formData)
          return
        }
        setWizardStepNumber(wizardStepNumber + 1)
      }
    })()
  }, [formData])

  // Effect to handle the modal button labels
  useEffect(() => {
    switch (wizardStepNumber) {
      case FORM_STEPS.FIRST:
        setPrevButtonLabel("Cancel")
        setNextButtonLabel("Next")
        break

      // case FORM_STEPS.SECOND:
      //   formMethods = step2Methods
      //   break

      case FORM_STEPS.THIRD:
        setPrevButtonLabel("Previous")
        setNextButtonLabel(!!company ? "Update" : "Create")
        break

      default:
        setPrevButtonLabel("Previous")
        setNextButtonLabel("Next")
        break
    }
  }, [wizardStepNumber])

  // Effect to change the UI if the update/create was success
  useEffect(() => {
    if (isSuccess) {
      setNextButtonLabel("Done")
      setShowCancelButton(false)
    }
  }, [isSuccess])

  const step1Methods = useForm(step1DefaultValues())
  // const step2Methods = useForm()
  const step3Methods = useForm()
  const step4Methods = useForm()

  const handleCompanySave = async (data: CompanyData) => {
    const companyObject: PostCompanyShape = {
      logo: data.logo,
      name: data.name,
      code: data.code,
      about: data.about,
      cover: data.cover,
      color: data.color.replaceAll("#", ""),
      status: company?.status || STATUS.INACTIVE,
      symbol: data.symbol,
      exchange: data.exchange?.value || "",
      location: {
        city: data.city,
        state: data.region?.value || "",
        postal: data.zipCode,
        country: data.country?.value || "",
      },
      description: data.description,
    }

    // Add the company ID if we are updating
    if (company) companyObject.id = company.id
    const companyLocation = companyObject.location as LocationShape
    setCompanyError(null!)

    try {
      setIsLoading(true)
      // Get or Create a location
      const locationUrl = await getLocationOrCreateOne(companyLocation)

      companyObject.logo = await processImage(
        "company",
        companyObject.logo,
        "logo",
        companyObject.code
      )
      companyObject.cover = await processImage(
        "company",
        companyObject.cover,
        "cover",
        companyObject.code
      )

      // Create Company object
      const companyData = {
        ...companyObject,
        contacts: [],
        location: locationUrl,
      }

      if (!company) {
        await mutatePostCompany(companyData)
      } else {
        await mutatePatchCompany(companyData)
      }
      setIsSuccess(true)
    } catch (error) {
      setCompanyError(error.message)
    }
  }

  const getLocationOrCreateOne = async (params: LocationShape) => {
    let locationUrl: string = ""
    try {
      const locations: LocationShape[] = await LocationService.getLocationsBy(
        params
      )
      if (locations?.length) {
        locationUrl = locations[0]._links?.self.href!
      } else {
        const newLocation: LocationShape = await LocationService.postLocation(
          params
        )
        locationUrl = newLocation._links?.self.href!
      }
    } catch (error) {
      setCompanyError(error.message)
    }

    return locationUrl
  }

  const onFormSubmit = (data: CompanyData) => {
    setFormData({ ...formData, ...data })
  }

  const onMoveStepBack = () => {
    const step = wizardStepNumber - 1
    setWizardStepNumber(step)

    if (step < FORM_STEPS.FIRST) {
      onRequestClose({ shouldRefreshList: false })
    }
  }

  const onMoveStepForward = () => {
    if (isSuccess) {
      onRequestClose({ shouldRefreshList: true })
      return
    }

    let formMethods: UseFormReturn<any>
    switch (wizardStepNumber) {
      case FORM_STEPS.FIRST:
        formMethods = step1Methods
        break

      case FORM_STEPS.SECOND:
        formMethods = step3Methods
        break

      case FORM_STEPS.THIRD:
        formMethods = step4Methods
        break
    }
    formMethods!.handleSubmit(onFormSubmit)()
  }

  return (
    <Modal
      isOpen={true}
      scrollable={true}
      buttonLabel={nextButtonLabel}
      cancelLabel={prevButtonLabel}
      cancelButton={showCancelButton}
      isButtonLoading={isLoading}
      onRequestClose={() => onRequestClose({ shouldRefreshList: false })}
      onRequestCancel={onMoveStepBack}
      onRequestConfirm={onMoveStepForward}
    >
      <WizardContainer>
        {!isSuccess && (
          <WizardTitle>
            {!!companyId ? "Edit a " : "Create New"} Company - Step{" "}
            {wizardStepNumber}/{FORM_STEPS.THIRD}
          </WizardTitle>
        )}

        <WizardForm>
          <FormProvider {...step1Methods}>
            <CompanyModalStep1
              visible={!isSuccess && wizardStepNumber === FORM_STEPS.FIRST}
            />
          </FormProvider>

          <FormProvider {...step3Methods}>
            <CompanyModalStep3
              visible={!isSuccess && wizardStepNumber === FORM_STEPS.SECOND}
            />
          </FormProvider>

          <FormProvider {...step4Methods}>
            <CompanyModalStep4
              visible={!isSuccess && wizardStepNumber === FORM_STEPS.THIRD}
            />
          </FormProvider>

          {isSuccess && (
            <ModalSuccessContainer>
              <CompletedIcon />

              <ModalSuccessTitle>
                Company successfully {!company ? "created" : "edited"}
              </ModalSuccessTitle>
            </ModalSuccessContainer>
          )}
        </WizardForm>
      </WizardContainer>

      <ErrorMessageSmall style={{ textAlign: "right" }}>
        {companyError}
      </ErrorMessageSmall>
    </Modal>
  )
}

export default CompanyModal
